"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, Check, Clock, Lock } from "@/components/icons";
import SectionHeader from "@/components/ui/section-header";
import { HeroGlow } from "@/components/ui";
import { STATS } from "@/lib/constants";
import FaqSection from "@/components/sections/products/shared/faq";
import CtaSection from "@/components/sections/shared/cta";
import {
  AccentIconChip,
  accentTextStyle,
  ToolCard,
  toolCardClass,
} from "@/components/sections/tools/_shared/parts";
import { PRIVACY_POINTS, TOOLS_FAQS, USE_CASES } from "./content";
import { AVAILABLE_TOOLS, COMING_SOON_TOOLS, PRODUCT_PAIRS, WHY_TOOLS } from "./data";

const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
};

const staticVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

/* Card icons are blue on every tile — one hue for the whole set, so a grid
   doesn't read as two families of card. The warm accent stays where it means
   something (the "Coming soon" pill) and in the washes underneath. */

function AvailableToolsHero() {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? staticVariants : itemVariants;

  return (
    /* overflow-y-clip, never x: the HeroGlow ribbon is 100vw wide and has to
       bleed past the shell's frame hairlines like every other hero on the site.
       Clipping x would cut the backdrop back to the 1280px content column. */
    <section className="border-border relative overflow-y-clip border-b pt-28 pb-16 sm:pt-32 sm:pb-20">
      <HeroGlow align="left" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: easeOut }}
          className="mb-12 max-w-2xl"
        >
          <SectionHeader
            as="h1"
            eyebrow="Tools"
            heading="Free proxy & network tools"
            body="A free toolkit for proxies, IPs and privacy. Browser Fingerprint, the WebRTC Leak Test, our Chrome and Firefox extensions and our MCP server are available now, with more tools on the way."
          />
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          animate="visible"
          className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3"
        >
          {AVAILABLE_TOOLS.map(({ icon: Icon, label, href, description }) => (
            <motion.div key={href} variants={variants} className="feature-tile">
              <ToolCard
                icon={Icon}
                titleAs="h2"
                title={label}
                description={description}
                badge={{ label: "Available" }}
                href={href}
                scroll={false}
                cta={{ label: "Open", style: "solid" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ComingSoonGrid() {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? staticVariants : itemVariants;

  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <SectionHeader
            eyebrow="Coming soon"
            heading="More tools on the way"
            body="A growing suite of free tools for proxies, IPs and privacy — no sign-up, nothing saved."
          />
        </div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
        >
          {COMING_SOON_TOOLS.map(({ icon: Icon, label, description }) => (
            <motion.div key={label} variants={variants} className="feature-tile">
              <ToolCard
                icon={Icon}
                title={label}
                description={description}
                /* The pill is the one warm element here — it carries the pending
                   state, where the icon is part of the uniform blue set. */
                badge={{ label: "Coming soon", icon: Clock, accent: "orange" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function WhyTools() {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? staticVariants : itemVariants;

  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <SectionHeader
            eyebrow="Why use them"
            heading="Built to be fast, free and private"
            body="The same principles behind Browser Fingerprint, our extensions and the tools still on the way."
          />
        </div>
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
        >
          {WHY_TOOLS.map((item) => (
            <motion.div key={item.title} variants={variants} className="feature-tile">
              <ToolCard icon={item.icon} title={item.title} description={item.description} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function UseCases() {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? staticVariants : itemVariants;

  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <SectionHeader
            eyebrow="Use cases"
            heading="What people use them for"
            body="A few common reasons to reach for the toolkit."
          />
        </div>
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
        >
          {USE_CASES.map(({ slug, text, links }) => (
            <motion.div key={slug} variants={variants} className="feature-tile">
              <div className={toolCardClass}>
                {/* Blue on every card here: a tick is a confirmation, and the
                    warm accent close to the amber warning tone would read as a
                    caution on hue alone. The wash underneath carries the
                    variety instead. */}
                <AccentIconChip icon={Check} size="sm" />
                <p className="text-foreground relative mt-4 text-base leading-relaxed">{text}</p>
                {links && (
                  <div className="relative mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-5">
                    {links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="inline-flex items-center gap-1 text-sm font-semibold transition-opacity hover:opacity-80"
                        style={accentTextStyle()}
                      >
                        {link.label}
                        <ArrowRight size={14} aria-hidden="true" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PrivacySection() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16"
        >
          <div>
            <SectionHeader
              eyebrow="Privacy & security"
              heading="Your data stays yours"
              body="The tools are built to inspect, not to collect. What you check stays with you."
            />
          </div>
          <div className="bg-card card-wash-blue border-border relative overflow-hidden rounded-2xl border p-6 sm:p-8">
            <div className="relative mb-6 flex items-center gap-3">
              <AccentIconChip icon={Lock} size="sm" />
              <h3 className="text-foreground text-lg font-semibold">Private by default</h3>
            </div>
            <ul className="relative space-y-4">
              {PRIVACY_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <Check
                    size={16}
                    className="mt-0.5 shrink-0"
                    style={accentTextStyle("blue")}
                    aria-hidden="true"
                  />
                  <span className="text-muted-foreground text-sm leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PairWithProxidize() {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? staticVariants : itemVariants;

  const stats = [
    { value: STATS.ips, label: "premium mobile & residential IPs" },
    { value: STATS.countries, label: "countries covered" },
    { value: STATS.uptime, label: "uptime" },
  ];

  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <SectionHeader
            eyebrow="Better together"
            heading="Pair them with Proxidize proxies"
            body="The tools work with proxies from any provider — but they're built by the team behind Proxidize's managed proxy network. Pair them with clean IPs for the best results."
          />
        </div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2"
        >
          {PRODUCT_PAIRS.map(({ icon: Icon, label, href, description }) => (
            <motion.div key={href} variants={variants} className="feature-tile">
              <ToolCard
                icon={Icon}
                title={label}
                description={description}
                href={href}
                cta={{ label: "Explore", style: "link" }}
              />
            </motion.div>
          ))}
        </motion.div>

        <div className="border-border bg-card card-wash-blue relative mt-10 grid grid-cols-1 divide-y overflow-hidden rounded-2xl border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {stats.map(({ value, label }) => (
            <div key={label} className="flex flex-col items-center gap-1 px-6 py-6 text-center">
              <span className="text-2xl font-bold tracking-tight" style={accentTextStyle("blue")}>
                {value}
              </span>
              <span className="text-muted-foreground text-sm">{label}</span>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
          Learn more about{" "}
          <Link
            href="/about"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            Proxidize, a managed cloud proxy provider
          </Link>{" "}
          built for developers, enterprises, and AI teams — or explore{" "}
          <Link
            href="/locations"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            proxy locations
          </Link>{" "}
          and{" "}
          <Link
            href="/use-cases"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            use cases
          </Link>
          , or read more on the{" "}
          <Link
            href="/blog"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            Proxidize blog
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

export default function ToolsHub() {
  return (
    /* No overflow clip here: the banded sections use `.full-bleed`, whose
       negative margins have to escape the shell's max-w-7xl frame to reach the
       viewport edges. html/body already carry overflow-x: clip, so 50vw stays
       scroll-safe — the hero section keeps its own clip for the glow. */
    <main id="main-content" tabIndex={-1} className="relative">
      <AvailableToolsHero />
      <ComingSoonGrid />
      <WhyTools />
      <UseCases />
      <PrivacySection />
      <PairWithProxidize />
      <FaqSection
        faqs={TOOLS_FAQS}
        description="Quick answers about the free Proxidize tools and browser extensions — pricing, privacy, and what's coming next."
        headingId="tools-faq-heading"
      />
      <CtaSection />
    </main>
  );
}
